import { ReactNode } from "react";

import {
  CheckRoundIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  MicFilledIcon,
  VideoCameraFilledIcon,
  VideoCameraSlashIcon,
} from "@vericus/cadmus-icons";
import { colors } from "@vericus/cadmus-ui";

import { assignInlineVars } from "@vanilla-extract/dynamic";

import { UseWebcamCaptureReturn } from "@/components/webcam-capture";

import * as styles from "./overlay.css";
import { useMicrophoneVolume } from "./use-microphone-volume";

interface OverlayProps {
  webcam: UseWebcamCaptureReturn;
  onToggleCamera?: () => void;
  cameraToggleDisabled?: boolean;
  showVolumeMeter?: boolean;
  /**
   * Escalates the microphone indicator: the track is live, but no audio is
   * coming through it. Set while a caller has a warning of its own on screen.
   */
  microphoneWarning?: boolean;
}

export const Overlay = ({
  webcam,
  onToggleCamera,
  cameraToggleDisabled = false,
  showVolumeMeter = true,
  microphoneWarning = false,
}: OverlayProps) => {
  const stream = webcam.stream;

  const hasAudioTrack =
    stream
      ?.getAudioTracks()
      .some((track) => track.readyState === "live" && track.enabled) ?? false;
  const { volumeLevel } = useMicrophoneVolume(showVolumeMeter ? stream : null);

  const isAudioOn = hasAudioTrack;
  const isVideoOn =
    stream
      ?.getVideoTracks()
      .some((track) => track.readyState === "live" && track.enabled) ?? false;

  // A microphone that is gone is the stronger and more specific state, so it
  // keeps the red indicator: telling a student to unmute a microphone that is no
  // longer there would be the wrong instruction. The warning only ever replaces
  // "the microphone is fine".
  const warnAboutMicrophone = microphoneWarning && isAudioOn;

  return (
    <div className={styles.container}>
      <div className={styles.indicatorsContainer}>
        <div
          className={styles.indicator({
            active: isAudioOn,
            warning: warnAboutMicrophone,
          })}
        >
          <IconWrapper>
            <MicFilledIcon
              label={microphoneLabel(isAudioOn, warnAboutMicrophone)}
              size={20}
              color="white"
            />
          </IconWrapper>
          {warnAboutMicrophone && (
            <div className={styles.miniStatus}>
              <div className={styles.miniStatusBackdrop}>
                <ExclamationTriangleIcon
                  label=""
                  size={12}
                  color={colors.orange500}
                />
              </div>
            </div>
          )}
          {!warnAboutMicrophone && isAudioOn && showVolumeMeter && (
            <div className={styles.miniStatus}>
              <CheckRoundIcon label="" size={16} color={colors.success} />
            </div>
          )}
          {!isAudioOn && (
            <div className={styles.miniStatus}>
              <ExclamationCircleIcon label="" size={16} color="#D92644" />
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.indicator({
            active: isVideoOn,
            cameraTurnedOff: webcam.cameraEnabled === false,
            interactive: true,
            disabled: cameraToggleDisabled,
          })}
          onClick={onToggleCamera}
          disabled={cameraToggleDisabled}
          aria-pressed={webcam.cameraEnabled}
          aria-label={
            webcam.cameraEnabled ? "Turn camera off" : "Turn camera on"
          }
        >
          <IconWrapper>
            {webcam.cameraEnabled === false ? (
              <VideoCameraSlashIcon label="" size={20} />
            ) : (
              <VideoCameraFilledIcon
                label=""
                size={20}
                color="white"
                fill="white"
              />
            )}
          </IconWrapper>
          {isVideoOn && showVolumeMeter && (
            <div className={styles.miniStatus}>
              <CheckRoundIcon label="" size={16} color={colors.success} />
            </div>
          )}
          {!isVideoOn && webcam.cameraEnabled !== false && (
            <div className={styles.miniStatus}>
              <ExclamationCircleIcon label="" size={16} color="#D92644" />
            </div>
          )}
        </button>
      </div>
      {showVolumeMeter && (
        <div className={styles.volumeMeterContainer}>
          <div
            className={styles.volumeMeter}
            style={assignInlineVars({
              [styles.volumeScale]: `${volumeLevel * 100}%`,
            })}
          >
            <div className={styles.volumeFill} />
          </div>
          <div className={styles.volumeMeterIcon}>
            <MicFilledIcon label="Microphone level" size={16} color="white" />
          </div>
        </div>
      )}
    </div>
  );
};

/** The only place the microphone's state is announced, so it carries the warning. */
const microphoneLabel = (isAudioOn: boolean, warning: boolean): string => {
  if (warning) return "Microphone on, but no sound is coming through";
  return isAudioOn ? "Microphone on" : "Microphone off";
};

const IconWrapper = ({ children }: { children: ReactNode }) => (
  <div className={styles.iconWrapper}>{children}</div>
);
